import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scenariosAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Play, 
  Clock, 
  Target,
  User,
  Heart,
  Spinner,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ScenarioDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScenario();
  }, [id]);

  const loadScenario = async () => {
    try {
      const response = await scenariosAPI.getById(id);
      setScenario(response.data);
    } catch (error) {
      console.error('Error loading scenario:', error);
      toast.error('Error al cargar el escenario');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSimulation = async () => {
    // Just navigate to simulation page - the role selection modal will be shown there
    navigate(`/simulation/${id}`);
  };

  const getLevelBadge = (level) => {
    const styles = {
      alta: 'bg-red-600 text-white',
      media: 'bg-yellow-500 text-white',
      baja: 'bg-green-500 text-white'
    };
    return styles[level] || 'bg-slate-500 text-white';
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  if (!scenario) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-slate-500">Escenario no encontrado</p>
          <Button variant="outline" onClick={() => navigate('/app/scenarios')} className="mt-4">
            Volver a escenarios
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/app/scenarios')}
          className="gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
          Volver a escenarios
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold font-['Chivo'] text-slate-900" data-testid="scenario-title">
                {scenario.nombre}
              </h1>
              <Badge className={getLevelBadge(scenario.nivel)}>
                Fidelidad {scenario.nivel}
              </Badge>
            </div>
            <p className="text-slate-500">
              Especialidad: <span className="font-medium capitalize">{scenario.especialidad?.replace('_', ' ')}</span>
            </p>
          </div>
          <Button 
            size="lg" 
            onClick={handleStartSimulation}
            className="gap-2"
            data-testid="start-simulation-btn"
          >
            <Play size={20} weight="fill" />
            Iniciar Simulación
          </Button>
        </div>

        {/* Patient Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User size={20} />
              Información del Paciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Edad</p>
                <p className="font-medium">{scenario.edad_paciente} años</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Sexo</p>
                <p className="font-medium">{scenario.sexo_paciente}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-slate-500">Antecedentes</p>
                <p className="font-medium">{scenario.antecedentes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Initial Vitals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart size={20} />
              Estado Inicial del Paciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">FC</p>
                <p className="text-2xl font-bold font-vital vital-fc">{scenario.estado_inicial?.fc}</p>
                <p className="text-xs text-slate-400">lpm</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">SpO2</p>
                <p className="text-2xl font-bold font-vital vital-spo2">{scenario.estado_inicial?.spo2}</p>
                <p className="text-xs text-slate-400">%</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">PA</p>
                <p className="text-2xl font-bold font-vital vital-pa">
                  {scenario.estado_inicial?.pa_sistolica}/{scenario.estado_inicial?.pa_diastolica}
                </p>
                <p className="text-xs text-slate-400">mmHg</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">FR</p>
                <p className="text-2xl font-bold font-vital vital-fr">{scenario.estado_inicial?.fr}</p>
                <p className="text-xs text-slate-400">rpm</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Temp</p>
                <p className="text-2xl font-bold font-vital vital-temp">{scenario.estado_inicial?.temperatura}</p>
                <p className="text-xs text-slate-400">°C</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Tiempo</p>
                <p className="text-2xl font-bold font-vital">{Math.floor(scenario.tiempo_maximo / 60)}</p>
                <p className="text-xs text-slate-400">min</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Objectives */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={20} />
              Objetivos de Aprendizaje
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {scenario.objetivos?.map((obj, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`objective-${i}`}>
                  <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{obj}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Critical Errors */}
        {scenario.evaluacion?.errores_criticos?.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Warning size={20} />
                Errores Críticos a Evitar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {scenario.evaluacion.errores_criticos.map((error, i) => (
                  <li key={i} className="flex items-start gap-2 text-red-700" data-testid={`critical-error-${i}`}>
                    <Warning size={18} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Time Info */}
        <div className="flex items-center justify-center gap-2 text-slate-500 py-4">
          <Clock size={18} />
          <span>Tiempo máximo de simulación: <strong>{Math.floor(scenario.tiempo_maximo / 60)} minutos</strong></span>
        </div>
      </div>
    </>
  );
}
